import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Paper, Grid, Button, IconButton, TextField, Modal, useMediaQuery, useTheme } from '@mui/material';
import { Edit, Lock } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';

const UserDetails = () => {
    const [user, setUser] = useState(null);
    const [editingProfile, setEditingProfile] = useState(false);
    const [editingPassword, setEditingPassword] = useState(false);

    const [editedName, setEditedName] = useState('');
    const [editedEmail, setEditedEmail] = useState('');
    const [editedPhoneNumber, setEditedPhoneNumber] = useState('');

    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');

    const fields = [
        { label: "Member No", field: "member_no" },
        { label: "Name", field: "name" },
        { label: "Individual Shares", field: "individual_shares" },
        { label: "Emergency Loan Balance", field: "emergency_loan_balance" },
        { label: "Normal Loan Balance", field: "normal_loan_balance" },
        { label: "Group Shares", field: "group_shares" },
        { label: "Welfare Funds", field: "welfare_funds" },
        { label: "Total Savings", field: "total_savings" },
        { label: "Principle", field: "principle" },
        { label: "Amount per Instalment", field: "amount_per_installment" },
        { label: "Number of Instalments", field: "number_of_installments" },
        { label: "Business Loan Balance", field: "business_loan_balance" },
        { label: "Project Loan Balance", field: "project_loan_balance" },
        { label: "Normal Loan Limit", field: "normal_loan_limit" },
        { label: "Penalty Due", field: "penalty_due" },
        { label: "Penalty Paid", field: "penalty_paid" },
        { label: "Group Dividends", field: "group_dividends" }
    ];
    
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        // Fetch user data from API using fetch
        fetch(`https://gracioussacco.co.ke/gracious/api/myuserdata.php?user_id=${localStorage.getItem('user_id')}`)
            .then((response) => response.json())
            .then((data) => {
                setUser(data);
            })
            .catch((error) => {
                console.error('There was an error fetching the user data!', error);
            });
    }, []);


    const handleEditProfile = () => {
        setUser({
            ...user,
            name: editedName || user.name,
            email: editedEmail || user.email,
            phoneNumber: editedPhoneNumber || user.phoneNumber,
        });
        setEditingProfile(false); // Close the modal after saving
    };

    const handleEditPassword = async () => {
        if (newPassword !== confirmNewPassword) {
            toast.error("New passwords don't match!");
            return;
        }
        if (!oldPassword || !newPassword) {
            toast.error("Please fill out all fields.");
            return;
        }

        try {

            const userId = localStorage.getItem('user_id');

            if (!userId) {
                toast.error("User ID is missing. Please log in again.");
                return;
            }
            const payload = {
                user_id: userId,
                old_password: oldPassword,
                new_password: newPassword,
            };

            // Make the PUT request
            const response = await fetch('https://gracioussacco.co.ke/gracious/api/auth.php', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            // Parse the response
            const data = await response.json();

            if (response.ok && data.success) {
                toast.success('Password updated successfully!');
                localStorage.clear();
                window.location.reload();
                setOldPassword('');
                setNewPassword('');
                setConfirmNewPassword('');
                setEditingPassword(false);
            } else {
                toast.error(data.message || 'Failed to update the password.');
            }
        } catch (error) {
            console.error('Error updating password:', error);
            toast.error('An error occurred. Please try again later.');
        }
    };



    if (!user) {
        return <Typography>Loading...</Typography>; // Show loading until user data is fetched
    }

    return (
        <Container maxWidth="sm">
            {/* Toast Container */}
            <ToastContainer />
            <Box sx={{ marginTop: 4 }}>
                {/* Profile Card with Email, Phone and Edit Icons */}
                <Paper sx={{ padding: 3, marginBottom: 3 }}>
                    <Typography variant="h6"
                        gutterBottom
                        sx={{
                            color: '#3f51b5',
                            fontWeight: 'bold',
                            fontSize: '1.2rem',
                            textTransform: 'uppercase',
                            marginBottom: 2,
                        }}>
                        My Profile
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <IconButton
                                edge="end"
                                color="primary"
                                onClick={() => setEditingProfile(true)}
                            >
                                <Edit />
                            </IconButton>
                            <IconButton
                                edge="end"
                                color="primary"
                                onClick={() => setEditingPassword(true)}
                            >
                                <Lock />
                            </IconButton>
                        </Box>
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} container justifyContent="space-between" alignItems="center">
                            <Typography variant="body1" fontWeight="bold">
                                Name:
                            </Typography>
                            <Typography variant="body1">{user.name}</Typography>
                        </Grid>
                        <Grid item xs={12} container justifyContent="space-between" alignItems="center">
                            <Typography variant="body1" fontWeight="bold">
                                Email:
                            </Typography>
                            <Typography variant="body1">{user.email}</Typography>
                        </Grid>

                        <Grid item xs={12} container justifyContent="space-between" alignItems="center">
                            <Typography variant="body1" fontWeight="bold">
                                Phone Number:
                            </Typography>
                            <Typography variant="body1">{user.phone}</Typography>
                        </Grid>
                    </Grid>
                </Paper>

                {/* User Details */}
                <Paper sx={{ padding: 3, marginTop: 3, marginBottom: 3 }}>
                    <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                            color: '#3f51b5',
                            fontWeight: 'bold',
                            fontSize: '1.2rem',
                            textTransform: 'uppercase',
                            marginBottom: 2,
                        }}
                    >
                        Sacco Details
                    </Typography>




                    <Grid container spacing={2}>
                        {fields.map(({ label, field }) => (
                            <Grid item xs={12} container justifyContent="space-between" alignItems="center" key={field}>
                                <Typography variant="body1" fontWeight="bold">
                                    {label}:
                                </Typography>
                                <Typography variant="body1">{user[field]}</Typography>
                            </Grid>
                        ))}
                    </Grid>


                </Paper>
            </Box>

            {/* Edit Profile Modal */}
            <Modal
                open={editingProfile}
                onClose={() => setEditingProfile(false)}
                aria-labelledby="edit-profile-modal"
                aria-describedby="edit-profile-description"
            >
                <Box sx={{
                    width: isMobile ? '90%' : 400,
                    margin: 'auto',
                    padding: 3,
                    backgroundColor: 'white',
                    marginTop: '10%',
                    borderRadius: 2
                }}>
                    <Typography variant="h6" gutterBottom>Edit Profile</Typography>

                    <TextField
                        label="Name"
                        fullWidth
                        margin="normal"
                        value={editedName || user.name}
                        onChange={(e) => setEditedName(e.target.value)}
                    />
                    <TextField
                        label="Email"
                        fullWidth
                        margin="normal"
                        value={editedEmail || user.email}
                        onChange={(e) => setEditedEmail(e.target.value)}
                    />
                    <TextField
                        label="Phone Number"
                        fullWidth
                        margin="normal"
                        value={editedPhoneNumber || user.phone}
                        onChange={(e) => setEditedPhoneNumber(e.target.value)}
                    />

                    <Box sx={{ marginTop: 2 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleEditProfile}
                            sx={{ width: '100%' }}
                        >
                            Save Changes
                        </Button>
                        <Button
                            variant="text"
                            color="secondary"
                            onClick={() => setEditingProfile(false)}
                            sx={{ width: '100%', marginTop: 2 }}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Edit Password Modal */}
            <Modal
                open={editingPassword}
                onClose={() => setEditingPassword(false)}
                aria-labelledby="edit-password-modal"
                aria-describedby="edit-password-description"
            >
                <Box sx={{
                    width: isMobile ? '90%' : 400,
                    margin: 'auto',
                    padding: 3,
                    backgroundColor: 'white',
                    marginTop: '10%',
                    borderRadius: 2
                }}>
                    <Typography variant="h6" gutterBottom>Change Password</Typography>

                    <TextField
                        label="Old Password"
                        type="password"
                        fullWidth
                        margin="normal"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                    />
                    <TextField
                        label="New Password"
                        type="password"
                        fullWidth
                        margin="normal"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <TextField
                        label="Confirm New Password"
                        type="password"
                        fullWidth
                        margin="normal"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                    />

                    <Box sx={{ marginTop: 2 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleEditPassword}
                            sx={{ width: '100%' }}
                        >
                            Save Password
                        </Button>
                        <Button
                            variant="text"
                            color="secondary"
                            onClick={() => setEditingPassword(false)}
                            sx={{ width: '100%', marginTop: 2 }}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Container>
    );
};

export default UserDetails;
